import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signOut, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  deleteUser, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  AuthError,
  connectAuthEmulator
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  setDoc,
  orderBy,
  serverTimestamp,
  connectFirestoreEmulator
} from 'firebase/firestore';
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  connectStorageEmulator
} from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validate Firebase config
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
];

const missingEnvVars = requiredEnvVars.filter(envVar => !import.meta.env[envVar]);

if (missingEnvVars.length > 0) {
  console.error('Missing Firebase environment variables:', missingEnvVars);
  throw new Error(`Missing Firebase configuration: ${missingEnvVars.join(', ')}`);
}

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Configure Firebase for better network handling
auth.settings.appVerificationDisabledForTesting = false;

// Export Firestore functions
export { doc, getDoc, collection, query, where, getDocs, updateDoc, orderBy, serverTimestamp };

// Export Storage functions
export { ref, uploadBytesResumable, getDownloadURL, deleteObject };

// Collection references
export const collabsRef = collection(db, 'collabs');
export const clientsRef = collection(db, 'clients');
export const influencersRef = collection(db, 'influencers');
export const usersRef = collection(db, 'users');
export const agenciesRef = collection(db, 'agencies');
export const resourcesRef = collection(db, 'resources');
export const castingRef = collection(db, 'castings');
export const todosRef = collection(db, 'todos');
export const recruitmentRef = collection(db, 'recruitment');
export const dailyBriefsRef = collection(db, 'dailyBriefs');
export const timelineItemsRef = collection(db, 'timelineItems');
export const collabDocumentsRef = collection(db, 'collabDocuments');
export const collabStatsRef = collection(db, 'collabStats');

// Auth functions
export const signIn = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error: any) {
    if (error.code === 'auth/invalid-credential') {
      throw new Error('Email ou mot de passe incorrect');
    }
    throw error;
  }
};

export const signUp = async (
  email: string, 
  password: string, 
  firstName: string,
  lastName: string,
  role: string,
  agencyName?: string,
  agencyCode?: string
) => {
  try {
    // Créez le compte utilisateur
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    const defaultPermissions = {
      collaborations: true,
      todo: true,
      clients: true,
      influencers: true,
      casting: true,
      accounting: true,
      resources: true,
      assistant: true
    };

    // Le super admin est automatiquement approuvé
    const isSuperAdmin = email === 'hosni@swipe-upp.com';
    const userStatus = isSuperAdmin ? 'approved' : 'pending';
    
    if (agencyName) {
      // Créer une nouvelle agence avec un ID spécifique
      const agencyRef = doc(agenciesRef, user.uid);
      const agencyData = {
        name: agencyName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ownerId: user.uid
      };
      
      // Créer l'agence
      await setDoc(agencyRef, agencyData);

      // Créer le profil utilisateur en tant qu'admin
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        firstName,
        lastName,
        role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        agencyId: user.uid,
        userRole: 'admin',
        status: userStatus,
        permissions: defaultPermissions
      });
    } else if (agencyCode) {
      // Vérifier si l'agence existe
      const agencyDoc = await getDoc(doc(db, 'agencies', agencyCode));
      if (!agencyDoc.exists()) {
        throw new Error('Cette agence n\'existe pas');
      }

      // Créer le profil utilisateur avec une agence existante
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        firstName,
        lastName,
        role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        agencyId: agencyCode,
        userRole: 'user',
        status: userStatus
      });
    } else {
      // Créer l'utilisateur sans agence
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        firstName,
        lastName,
        role,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userRole: 'user',
        status: userStatus,
        permissions: defaultPermissions
      });
    }

    // Si l'utilisateur n'est pas super admin, il doit attendre l'approbation
    if (!isSuperAdmin) {
      console.log('Compte créé en attente d\'approbation pour:', email);
    }

    return userCredential;
  } catch (error: any) {
    // Gestion d'erreurs spécifique
    if ((error as AuthError).code === 'auth/email-already-in-use') {
      throw new Error('Un compte existe déjà avec cette adresse email');
    }
    console.error('Erreur lors de l\'inscription:', error);
    throw error;
  }
};

export const logout = () => signOut(auth);

export const deleteAccount = async (password: string) => {
  const user = auth.currentUser;
  if (!user || !user.email) {
    throw new Error('Aucun utilisateur connecté');
  }

  try {
    // Re-authenticate user before deletion
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);

    // Delete user data from Firestore
    await deleteDoc(doc(db, 'users', user.uid));

    // Delete the user account
    await deleteUser(user);
  } catch (error: any) {
    if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
      throw new Error('Mot de passe incorrect');
    }
    throw error;
  }
};

export const joinAgency = async (agencyCode: string) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Aucun utilisateur connecté');
  }

  try {
    // Verify the agency exists
    const agencyDoc = await getDoc(doc(db, 'agencies', agencyCode));
    if (!agencyDoc.exists()) {
      throw new Error('Cette agence n\'existe pas');
    }

    // Update the user profile with the agency ID using setDoc instead of updateDoc
    await setDoc(doc(db, 'users', user.uid), {
      agencyId: agencyCode,
      userRole: 'user',
      updatedAt: serverTimestamp()
    }, { merge: true }); // Use merge option to update only specified fields

    return { success: true };
  } catch (error: any) {
    console.error('Error joining agency:', error);
    if (error.message === 'Cette agence n\'existe pas') {
      throw error;
    }
    throw new Error('Une erreur est survenue lors de la tentative de rejoindre l\'agence');
  }
};

// Types
export const BUSINESS_SECTORS = [
  {
    value: 'agency',
    label: 'Agence',
    subcategories: [
      { value: 'influence', label: 'Influence' },
      { value: 'pr', label: 'Relations Publiques' },
      { value: 'digital', label: 'Marketing Digital' },
      { value: 'creative', label: 'Créative' },
      { value: 'media', label: 'Média' },
      { value: 'event', label: 'Événementiel' },
      { value: 'communication', label: 'Communication' }
    ]
  },
  {
    value: 'family',
    label: 'Famille & Maternité',
    subcategories: [
      { value: 'maternity', label: 'Maternité' },
      { value: 'baby', label: 'Bébé' },
      { value: 'kids', label: 'Enfants' },
      { value: 'education', label: 'Éducation' },
      { value: 'parenting', label: 'Parentalité' }
    ]
  },
  {
    value: 'tech_apps',
    label: 'Tech & Applications',
    subcategories: [
      { value: 'mobile_apps', label: 'Applications Mobiles' },
      { value: 'saas', label: 'SaaS' },
      { value: 'fintech', label: 'FinTech' },
      { value: 'edtech', label: 'EdTech' },
      { value: 'iot', label: 'IoT' }
    ]
  },
  {
    value: 'gaming',
    label: 'Gaming & eSport',
    subcategories: [
      { value: 'video_games', label: 'Jeux Vidéo' },
      { value: 'esport', label: 'eSport' },
      { value: 'streaming', label: 'Streaming' },
      { value: 'gaming_hardware', label: 'Matériel Gaming' },
      { value: 'mobile_gaming', label: 'Gaming Mobile' }
    ]
  },
  {
    value: 'health',
    label: 'Santé & Bien-être',
    subcategories: [
      { value: 'medical', label: 'Médical' },
      { value: 'mental_health', label: 'Santé Mentale' },
      { value: 'nutrition', label: 'Nutrition' },
      { value: 'pharmacy', label: 'Pharmacie' },
      { value: 'wellness', label: 'Bien-être' }
    ]
  },
  {
    value: 'home',
    label: 'Maison & Décoration',
    subcategories: [
      { value: 'furniture', label: 'Mobilier' },
      { value: 'decoration', label: 'Décoration' },
      { value: 'garden', label: 'Jardin' },
      { value: 'diy', label: 'DIY' },
      { value: 'smart_home', label: 'Maison Connectée' }
    ]
  },
  {
    value: 'finance',
    label: 'Finance & Investissement',
    subcategories: [
      { value: 'banking', label: 'Banque' },
      { value: 'insurance', label: 'Assurance' },
      { value: 'investment', label: 'Investissement' },
      { value: 'crypto', label: 'Crypto-monnaies' },
      { value: 'personal_finance', label: 'Finance Personnelle' }
    ]
  },
  {
    value: 'fashion',
    label: 'Mode et Beauté',
    subcategories: [
      { value: 'ready_to_wear', label: 'Prêt-à-porter' },
      { value: 'accessories', label: 'Accessoires' },
      { value: 'cosmetics', label: 'Cosmétiques' },
      { value: 'perfumes', label: 'Parfumerie' },
      { value: 'jewelry', label: 'Bijoux' },
      { value: 'hair_care', label: 'Soins capillaires' }
    ]
  },
  {
    value: 'food',
    label: 'Alimentation et Boissons',
    subcategories: [
      { value: 'soft_drinks', label: 'Boissons gazeuses' },
      { value: 'alcoholic_beverages', label: 'Boissons alcoolisées' },
      { value: 'organic_food', label: 'Alimentation bio' },
      { value: 'snacks', label: 'Snacks et confiseries' },
      { value: 'dairy', label: 'Produits laitiers' }
    ]
  },
  {
    value: 'pet_food',
    label: 'Alimentation pour Animaux',
    subcategories: [
      { value: 'dry_food', label: 'Croquettes' },
      { value: 'wet_food', label: 'Nourriture humide' },
      { value: 'supplements', label: 'Compléments alimentaires' },
      { value: 'accessories', label: 'Accessoires pour animaux' },
      { value: 'care_products', label: 'Produits de soin' }
    ]
  },
  {
    value: 'tech',
    label: 'Tech et Digital',
    subcategories: [
      { value: 'software', label: 'Logiciels' },
      { value: 'mobile_apps', label: 'Applications mobiles' },
      { value: 'hardware', label: 'Matériel informatique' },
      { value: 'consumer_electronics', label: 'Électronique grand public' },
      { value: 'iot', label: 'Internet des objets (IoT)' },
      { value: 'ecommerce', label: 'E-commerce' }
    ]
  },
  {
    value: 'lifestyle',
    label: 'Lifestyle et Bien-être',
    subcategories: [
      { value: 'fitness', label: 'Fitness et musculation' },
      { value: 'meditation', label: 'Méditation et relaxation' },
      { value: 'healthy_food', label: 'Alimentation saine' },
      { value: 'sportswear', label: 'Vêtements de sport' },
      { value: 'yoga', label: 'Articles de yoga' },
      { value: 'mental_health', label: 'Santé mentale' }
    ]
  },
  {
    value: 'travel',
    label: 'Voyage et Tourisme',
    subcategories: [
      { value: 'travel_agencies', label: 'Agences de voyages' },
      { value: 'hotels', label: 'Hôtels et hébergement' },
      { value: 'transport', label: 'Transport (avion, train, voiture)' },
      { value: 'activities', label: 'Activités de loisirs et excursions' },
      { value: 'cruises', label: 'Croisières' }
    ]
  },
  {
    value: 'sport',
    label: 'Sport et Fitness',
    subcategories: [
      { value: 'equipment', label: 'Equipements sportifs' },
      { value: 'clothing', label: 'Vêtements de sport' },
      { value: 'nutrition', label: 'Nutrition sportive' },
      { value: 'accessories', label: 'Accessoires de fitness' },
      { value: 'clubs', label: 'Clubs de sport' }
    ]
  },
  {
    value: 'luxury',
    label: 'Luxe',
    subcategories: [
      { value: 'high_end_clothing', label: 'Vêtements haut de gamme' },
      { value: 'watches', label: 'Montres' },
      { value: 'cars', label: 'Voitures de luxe' },
      { value: 'jewelry', label: 'Joaillerie' },
      { value: 'accessories', label: 'Accessoires de luxe' },
      { value: 'perfumes', label: 'Parfumerie de luxe' }
    ]
  },
  {
    value: 'automotive',
    label: 'Automobile',
    subcategories: [
      { value: 'cars', label: 'Voitures' },
      { value: 'parts', label: 'Pièces et accessoires auto' },
      { value: 'insurance', label: 'Assurance automobile' },
      { value: 'rental', label: 'Location de véhicules' },
      { value: 'electric', label: 'Véhicules électriques' }
    ]
  },
  {
    value: 'entertainment',
    label: 'Divertissement',
    subcategories: [
      { value: 'movies', label: 'Films et séries' },
      { value: 'gaming', label: 'Jeux vidéo' },
      { value: 'music', label: 'Musique' },
      { value: 'theme_parks', label: 'Parcs d\'attractions' },
      { value: 'festivals', label: 'Festivals' }
    ]
  }
] as const;

export interface UserProfile {
  id?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  agencyId?: string;
  userRole: 'admin' | 'user';
  status: 'pending' | 'approved' | 'rejected';
  permissions?: {
    collaborations: boolean;
    todo: boolean;
    clients: boolean;
    influencers: boolean;
    casting: boolean;
    accounting: boolean;
    resources: boolean;
    assistant: boolean;
  };
  createdAt: Date;
  updatedAt?: Date;
}

export interface Agency {
  id?: string;
  name: string;
  ownerId: string;
  createdAt: Date;
  updatedAt?: Date;
}

export interface Todo {
  id?: string;
  agencyId: string;
  userId: string;
  assignedTo?: string;
  title: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high';
  dueDate?: Date;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface DailyBrief {
  id?: string;
  agencyId: string;
  influencerId: string;
  influencerName: string;
  influencerSlug: string; // URL-friendly version of name
  agencyName: string;
  date: Date;
  tasks: {
    id: string;
    text: string;
    completed: boolean;
    completedAt?: Date;
  }[];
  internalNotes: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface RecruitmentInfluencer {
  id?: string;
  agencyId: string;
  fullName: string;
  username: string;
  instagramUrl?: string;
  tiktokUrl?: string;
  youtubeUrl?: string;
  internalRating: number; // 1-5 stars
  tags: string[];
  collaboratedBrands: string;
  status: 'spotted' | 'contacted' | 'validated' | 'refused';
  campaignId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Contact {
  name: string;
  email: string;
  phone: string;
  type: 'brand' | 'agency';
  role?: string;
}

export interface Client {
  id?: string;
  agencyId: string;
  brand_name: string;
  sector: {
    main: string;
    sub: string;
  };
  contacts: Contact[];
  notes: string;
  collabs_ids?: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface Platform {
  name: 'Instagram' | 'TikTok' | 'YouTube' | 'Twitch' | 'Snapchat';
  audience: string;
  handle: string;
}

export interface ContentRate {
  type: 'post_instagram' | 'story_instagram' | 'reel_instagram' | 'tiktok' | 'story_snapchat' | 'twitch' | 'youtube';
  price: string;
}

export interface Influencer {
  id?: string;
  agencyId: string;
  agencyName?: string;
  fullName: string;
  username: string;
  address: string;
  phoneNumber: string;
  birthDate: string;
  siret: string;
  platforms: Platform[];
  internalReferent: string;
  rates: ContentRate[];
  isExclusive: boolean;
  publicToken?: string;
  publicLinkEnabled?: boolean;
  proposedTo: string[];
  internalNotes: string;
  archived: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Resource {
  id?: string;
  agencyId: string;
  name: string;
  type: 'brief' | 'contract' | 'email' | 'training' | 'video' | 'other';
  description: string;
  url?: string;
  fileUrl?: string;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Deliverable {
  type: string;
  description: string;
  price: string;
}

export interface CollabInfluencer {
  id?: string;
  fullName: string;
  deliverables: Deliverable[] | any[];
  rate: string;
  creator_invoice_sent: boolean;
  paidPercentage?: number;
}

export interface Collab {
  id?: string;
  agencyId: string;
  agencyName?: string;
  createdBy?: string;
  name: string | any;
  date: Date;
  brand: string | any;
  influencers: CollabInfluencer[] | any[];
  influencerIds?: string[];
  quote_sent: boolean;
  quote_number?: string;
  contract_signed: boolean;
  manager: string;
  post_progress: '0' | '30' | '50' | '100';
  billing_status: string;
  invoice_number?: string;
  payment_received: boolean;
  type: 'collaboration_entrante' | 'collaboration_sortante' | 'casting_interne' | 'casting_externe';
  publicToken?: string;
  publicLinkEnabled?: boolean;
  budget?: number;
  linkToken?: string;
  linkActive?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface TimelineItem {
  id?: string;
  collabId: string;
  stepName: string;
  deadline?: Date;
  status: 'todo' | 'in_progress' | 'done' | 'late';
  order: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CollabDocument {
  id?: string;
  collabId: string;
  name: string;
  type: 'brief' | 'contract' | 'other';
  fileUrl: string;
  fileName: string;
  fileSize: number;
  visibleForBrand: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface CollabStats {
  id?: string;
  collabId: string;
  postThumb?: string;
  postUrl?: string;
  views: number;
  likes: number;
  engagementRate: number;
  creator: string;
  createdAt: Date;
  updatedAt: Date;
}
export interface CastingBrief {
  id?: string;
  agencyId: string;
  brand: string;
  name: string;
  description: string;
  requirements: string[];
  budget: string;
  startDate: Date;
  endDate: Date;
  status: 'draft' | 'active' | 'closed';
  selectedInfluencers: string[];
  proposedInfluencers: {
    influencerId: string;
    fullName: string;
    status: 'pending' | 'accepted' | 'rejected';
    notes?: string;
  }[];
  createdAt: Date;
  updatedAt: Date;
}

// CRUD Functions with Agency Filtering
export const createDocument = async <T extends { agencyId: string }>(
  collectionRef: any,
  data: T
): Promise<T & { id: string; _exists?: boolean }> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Aucun utilisateur connecté');
    }

    // Vérifier si un document similaire existe déjà pour éviter les doublons
    const existingDocs = await getDocs(collectionRef);
    const existingData = existingDocs.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Vérifier les doublons potentiels basés sur des critères spécifiques
    if (collectionRef === collabsRef && 'name' in data && 'brand' in data) {
      const potentialDuplicate = existingData.find(doc => 
        doc.name === data.name && 
        doc.brand === data.brand &&
        doc.agencyId === data.agencyId
      );
      
      if (potentialDuplicate) {
        console.warn('Document potentiellement dupliqué détecté:', potentialDuplicate.id);
        return { ...potentialDuplicate, _exists: true } as T & { id: string; _exists: boolean };
      }
    }

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      throw new Error('Profil utilisateur non trouvé');
    }

    const userData = userDoc.data() as UserProfile;
    if (!userData.agencyId) {
      throw new Error('Aucune agence associée à cet utilisateur');
    }
    
    const docData = {
      ...data,
      agencyId: userData.agencyId,
      createdBy: user.uid,
      _timestamp: Date.now(), // Ajouter un timestamp unique pour éviter les doublons
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Pour les collections qui nécessitent le nom de l'agence, l'ajouter automatiquement
    if (collectionRef === influencersRef || collectionRef === collabsRef) {
      try {
        const agencyDoc = await getDoc(doc(db, 'agencies', userData.agencyId));
        if (agencyDoc.exists()) {
          docData.agencyName = agencyDoc.data().name || '';
        }
      } catch (agencyError) {
        console.warn('Impossible de récupérer le nom de l\'agence:', agencyError);
      }
    }
    
    const docRef = await addDoc(collectionRef, docData);
    return { id: docRef.id, ...docData };
  } catch (error) {
    console.error('Erreur lors de la création du document:', error);
    throw error;
  }
};

export const getDocuments = async <T extends object>(
  collectionRef: any,
  whereClause?: { field: string; operator: any; value: any }
): Promise<(T & { id: string; _exists: boolean })[]> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('No user logged in');
      return []; // Return empty array if no user is logged in
    }

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      console.log('User profile not found');
      return []; // Return empty array if user profile doesn't exist
    }

    const userData = userDoc.data() as UserProfile;
    console.log('User data:', {
      uid: user.uid,
      email: user.email,
      agencyId: userData.agencyId
    });
    
    let baseQuery;
    
    // Special handling for todos collection
    if (collectionRef === todosRef) {
      if (user.email === 'hosni@swipe-upp.com') { // Super admin sees only their todos
        console.log('Super admin access - fetching personal todos');
        baseQuery = query(
          collectionRef,
          where('userId', '==', user.uid)
        );
      } else {
        console.log('User access - fetching user todos and assigned todos');
        baseQuery = query(
          collectionRef,
          where('agencyId', '==', userData.agencyId),
          where('assignedTo', '==', user.uid)
        );
      }
    } else {
      console.log('Regular collection query with agencyId filter');
      // Super admin can see all documents
      if (user.email === 'hosni@swipe-upp.com') {
        baseQuery = collectionRef;
      } else {
        baseQuery = query(collectionRef, where('agencyId', '==', userData.agencyId));
      }
    }
    
    const querySnapshot = await getDocs(baseQuery);
    
    // Utiliser un Set pour suivre les IDs déjà traités et éviter les doublons
    const processedIds = new Set<string>();
    const results: (T & { id: string; _exists: boolean })[] = [];
    
    // Traiter les documents principaux
    for (const doc of querySnapshot.docs) {
      if (!processedIds.has(doc.id)) {
        processedIds.add(doc.id);
        results.push({
          id: doc.id,
          ...doc.data(),
          _exists: true // Marquer comme existant dans Firestore
        } as T & { id: string; _exists: boolean });
      }
    }
    
    // For todos, also fetch todos assigned to the user
    if (collectionRef === todosRef) {
      const userTodosQuery = query(
        collectionRef,
        where('assignedTo', '==', user.uid)
      );
      const userTodosSnapshot = await getDocs(userTodosQuery);
      
      // Traiter les tâches assignées à l'utilisateur
      for (const doc of userTodosSnapshot.docs) {
        if (!processedIds.has(doc.id)) {
          processedIds.add(doc.id);
          results.push({
            id: doc.id,
            ...doc.data(),
            _exists: true // Marquer comme existant dans Firestore
          } as T & { id: string; _exists: boolean });
        }
      }
    }
    
    console.log(`Total documents retrieved: ${results.length} (after deduplication)`);
    console.log('Total results:', results.length);
    return results.filter(doc => doc._exists === true); // Ne retourner que les documents qui existent réellement
  } catch (error) {
    console.error('Error getting documents:', error);
    console.log('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    return []; // Retourner un tableau vide en cas d'erreur
  }
};

export const updateDocument = async <T extends object>(
  collectionRef: any,
  id: string,
  data: Partial<T>,
  skipAuthCheck: boolean = false,
): Promise<{ id: string } & Partial<T>> => {
  try {
    console.log(`Updating document in ${collectionRef.path} with ID ${id}`);
    
    // Vérifier si le document existe avant de le mettre à jour
    const docRef = doc(db, collectionRef.path, id);
    const docSnap = await getDoc(docRef);
    
    // Vérifier si le document existe réellement
    if (!docSnap.exists()) {
      console.error(`Document with ID ${id} not found in ${collectionRef.path}`);
      throw new Error('Document not found');
    }
    
    if (!docSnap.exists()) {
      console.error(`Document with ID ${id} not found in ${collectionRef.path}`);
      throw new Error('Document not found');
    }
    
    if (!skipAuthCheck) {
      const user = auth.currentUser;
      if (!user) {
        console.error('No user logged in');
        throw new Error('Aucun utilisateur connecté');
      }

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) {
        console.error('User profile not found');
        throw new Error('Profil utilisateur non trouvé');
      }

      const userData = userDoc.data() as UserProfile;
      if (!userData.agencyId) {
        console.error('No agency associated with user');
        throw new Error('Aucune agence associée à cet utilisateur');
      }
      
      const docData = docSnap.data();
      if (docData.agencyId !== userData.agencyId && user.email !== 'hosni@swipe-upp.com') {
        console.error(`User's agency (${userData.agencyId}) doesn't match document's agency (${docData.agencyId})`);
        throw new Error('Unauthorized');
      }
    }
    
    try {
      // Créer une copie des données pour éviter les problèmes de référence
      const dataCopy = JSON.parse(JSON.stringify(data));
      
      // Supprimer les propriétés qui ne doivent pas être stockées
      if (dataCopy._exists) delete dataCopy._exists;
      if (dataCopy._timestamp) delete dataCopy._timestamp;
      
      console.log(`Updating document with data:`, dataCopy);
      
      await updateDoc(docRef, { 
        ...dataCopy, 
        updatedAt: serverTimestamp()
      });

      // Vérifier que la mise à jour a bien été effectuée
      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Document not found after update');
      }
      
      console.log(`Document updated successfully`);
      return { id, ...dataCopy };
    } catch (updateError) {
      console.error('Error in updateDoc operation:', updateError);
      throw updateError;
    }
  } catch (error: any) {
    console.error('Error updating document:', error);
    throw error;
  }
};

export const deleteDocument = async (
  collectionRef: any,
  id: string
): Promise<{ success: boolean; id: string; message?: string }> => {
  const startTime = Date.now();
  try {
    console.log(`[${new Date().toISOString()}] Attempting to delete document with ID ${id} from ${collectionRef.path}`);
    const user = auth.currentUser;
    if (!user) {
      console.error('No user logged in');
      throw new Error('Aucun utilisateur connecté');
    }
    
    // Vérifier si le document existe avant de tenter de le supprimer
    const docRef = doc(db, collectionRef.path, id);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.warn(`Document with ID ${id} not found - it may have been already deleted`);
      return { success: true, id, message: 'Document already deleted' };
    }
    
    console.log(`Document exists and will be deleted: ${id}`);

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) {
      console.error('User profile not found');
      throw new Error('Profil utilisateur non trouvé');
    }

    const userData = userDoc.data() as UserProfile;
    const isSuperAdmin = user.email === 'hosni@swipe-upp.com';
    
    if (!userData.agencyId && !isSuperAdmin) {
      console.error(`No agency associated with user ${user.uid}`);
      throw new Error(`Aucune agence associée à cet utilisateur ${user.uid}`);
    }
    
    const docData = docSnap.data();
    
    // Vérifier les autorisations (super admin ou membre de l'agence)
    if (!isSuperAdmin && docData.agencyId !== userData.agencyId) {
      console.error(`User's agency (${userData.agencyId}) doesn't match document's agency (${docData.agencyId})`);
      throw new Error('Vous n\'êtes pas autorisé à supprimer ce document');
    }

    // If the document has a fileUrl, delete the file from storage
    if (docData.fileUrl) {
      try {
        const fileRef = ref(storage, docData.fileUrl);
        await deleteObject(fileRef);
        console.log(`Associated file deleted: ${docData.fileUrl}`);
      } catch (fileError) {
        console.error('Error deleting associated file:', fileError);
        // Continue with document deletion even if file deletion fails
      }
    }
    
    try {
      await deleteDoc(docRef);
      console.log(`Document with ID ${id} successfully deleted`);
      console.log(`Document with ID ${id} successfully deleted from ${collectionRef.path} in ${Date.now() - startTime}ms`);
      
      // Vérifier que le document a bien été supprimé
      const verifyDoc = await getDoc(docRef);
      if (!verifyDoc.exists()) {
        console.log(`Verified: Document ${id} no longer exists`);
      } else {
        console.warn(`Warning: Document ${id} still exists after deletion attempt`);
        throw new Error(`Document ${id} still exists after deletion attempt`);
      }
    } catch (deleteError) {
      console.error('Error in deleteDoc operation:', deleteError);
      throw deleteError;
    }
    
    return { success: true, id, message: 'Document successfully deleted' };
  } catch (error) {
    console.error('Error deleting document:', error);
    console.log('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    throw error;
  }
};

// Fonction pour vérifier si un document existe
export const documentExists = async (collectionRef: any, id: string): Promise<boolean> => {
  try {
    if (!id) return false;
    
    const docRef = doc(db, collectionRef.path, id);
    const docSnap = await getDoc(docRef);
    return docSnap.exists();
  } catch (error) {
    console.error('Error checking if document exists:', error);
    return false;
  }
};